import { Position, Positions } from '@agilox/ui-common';
import { Component, computed, ElementRef, model, signal, Signal, viewChild } from '@angular/core';

/**
 * This component is positioned by its respective directive. It should never be used alone and therefore it is not exposed by the public api
 */
@Component({
	selector: 'ui-tooltip',
	templateUrl: './tooltip.component.html',
	standalone: false,
})
export class TooltipComponent {
	position = model<Position>(Positions.Top);
	tooltip = model<string>('');
	caretX = model<number>(0);
	caretY = model<number>(0);
	visible = model<boolean>(false);
	styleClass = model<string>('');

	tooltipElement = viewChild<ElementRef | undefined>('tooltipEl');
	tooltipContent = viewChild<ElementRef | undefined>('tooltipContent');

	styleClasses: Signal<string> = computed(() => {
		const classes = [];

		if (this.visible()) {
			classes.push('opacity-100', 'transition-opacity', 'duration-300');
		}

		if (this.position() === Positions.Top && this.caretX() && !this.top()) {
			classes.push('-translate-x-1/2');
		}

		classes.push(this.getMargin());

		switch (this.position()) {
			case Positions.Top:
				classes.push('-translate-y-full');
				break;
			case Positions.Left:
				classes.push('-translate-x-full');
				break;
		}

		classes.push(this.styleClass());

		return classes.join(' ');
	});

	caretStyleClasses: Signal<string> = computed(() => {
		const classes = [];

		if (this.visible()) {
			classes.push('opacity-100', 'transition-opacity', 'duration-300');
		}

		classes.push(this.getMargin());

		switch (this.position()) {
			case Positions.Top:
				classes.push(
					'-translate-x-1/2',
					'border-b-0',
					'border-l-transparent',
					'border-r-transparent',
					'border-t-secondary-800'
				);
				break;

			case Positions.Bottom:
				classes.push(
					'-translate-x-1/2',
					'-translate-y-full',
					'border-l-transparent',
					'border-r-transparent',
					'border-t-0',
					'border-b-secondary-800'
				);
				break;

			case Positions.Left:
				classes.push(
					'-translate-y-1/2',
					'border-b-transparent',
					'border-t-transparent',
					'border-r-0',
					'border-l-secondary-800'
				);
				break;

			case Positions.Right:
				classes.push(
					'-translate-x-full',
					'-translate-y-1/2',
					'border-b-transparent',
					'border-t-transparent',
					'border-l-0',
					'border-r-secondary-800'
				);
				break;
		}

		return classes.join(' ');
	});

	calculatedTop = computed(() => {
		return this.top() ? this.top() + 'px' : this.caretY() ? this.caretY() + 'px' : '';
	});

	calculatedLeft = computed(() => {
		return this.left() ? this.left() + 'px' : this.caretX() ? this.caretX() + 'px' : '';
	});

	left = signal<number | undefined>(undefined);
	top = signal<number | undefined>(undefined);

	public readonly Positions = Positions;

	getMargin(): string {
		switch (this.position()) {
			case Positions.Top: {
				return '-mt-2.5';
			}
			case Positions.Bottom: {
				return 'mt-2.5';
			}
			case Positions.Left: {
				return '-ml-2.5';
			}
			case Positions.Right: {
				return 'ml-2.5';
			}
		}
	}
}
